import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, X } from 'lucide-react';

const CASE_STUDIES_PARENT_URL = `${process.env.PUBLIC_URL || ''}/case-studies/index.json`;

/** Escape HTML so we can safely inject <strong> and <br /> only. */
function escapeHtml(s) {
  if (typeof s !== 'string') return '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Turn simple markdown (paragraphs, **bold**, newlines) into safe HTML string.
 * Used for section content from JSON.
 */
function contentToHtml(content) {
  if (!content || typeof content !== 'string') return '';
  const escaped = escapeHtml(content);
  const withBold = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  const withBreaks = withBold.replace(/\n/g, '<br/>');
  const paragraphs = withBreaks.split(/<br\/><br\/>/).filter(Boolean);
  return paragraphs.map((p) => `<p>${p}</p>`).join('');
}

/**
 * Case study detail (#casestudy/slug). Fetches parent JSON to get contentPath, then fetches child JSON.
 * Renders whatever the JSON contains: title, introduction, description, image, explanation, sections (id/title/content or heading/subHeading/explanation/imageUrl/drawioUrl), keyTakeaways.
 */
function resolveImgUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${process.env.PUBLIC_URL || ''}${url}`;
}

export default function CaseStudyDetail({ slug, onBack }) {
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const closeLightbox = useCallback(() => setLightboxSrc(null), []);
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc, closeLightbox]);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(CASE_STUDIES_PARENT_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Parent not found');
        return res.json();
      })
      .then((parent) => {
        const item = (parent.items || []).find((i) => i.slug === slug);
        const contentPath = item?.contentPath;
        if (!contentPath || cancelled) {
          if (!contentPath) setError('Not found');
          setLoading(false);
          return;
        }
        const url = contentPath.startsWith('http') ? contentPath : `${process.env.PUBLIC_URL || ''}${contentPath}`;
        return fetch(url).then((r) => {
          if (!r.ok) throw new Error('Not found');
          return r.json();
        });
      })
      .then((data) => {
        if (data && !cancelled) setPaper(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 pb-12 flex items-center justify-center">
        <p className="font-body text-text-sub">Loading…</p>
      </div>
    );
  }

  if (error || !paper) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <p className="font-body text-text-sub">Case study not found.</p>
        </div>
      </div>
    );
  }

  const {
    title,
    introduction,
    description,
    imageUrl,
    imageCaption,
    explanation,
    date,
    sections = [],
    keyTakeaways = [],
  } = paper;

  const hasNewSections = sections.some((s) => 'content' in s || ('title' in s && !('heading' in s)));

  return (
    <div className="min-h-screen bg-forest-bg pt-24 pb-12">
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Case studies
        </button>
        {date && <p className="font-mono text-text-muted text-sm mb-2">{date}</p>}
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-text-hero mb-4">{title}</h1>
        {introduction && <p className="font-body text-lg text-text-sub mb-6">{introduction}</p>}
        {description && <p className="font-body text-text-body mb-8">{description}</p>}
        {imageUrl && (
          <figure className="my-8 rounded-xl overflow-hidden border bg-forest-card p-4" style={{ borderColor: 'var(--jb-border)' }}>
            <button type="button" onClick={() => setLightboxSrc(resolveImgUrl(imageUrl))} className="block w-full text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-forest-bg rounded-lg">
              <img src={resolveImgUrl(imageUrl)} alt="" className="w-full h-auto" />
            </button>
            {imageCaption && <figcaption className="font-body text-sm text-text-muted mt-2">{imageCaption}</figcaption>}
          </figure>
        )}
        {!imageUrl && imageCaption && <p className="font-body text-sm text-text-muted italic mb-6">{imageCaption}</p>}
        {explanation && !hasNewSections && (
          <section className="mt-8">
            <h2 className="font-display font-bold text-xl text-text-hero mb-3">Explanation & takeaways</h2>
            <div className="font-body text-text-body whitespace-pre-line">{explanation}</div>
          </section>
        )}

        {/* Agnostic sections: new format (id, title, content) or legacy (heading, subHeading, explanation, imageUrl, drawioUrl) */}
        {sections && sections.length > 0 && (
          <div className="mt-10 space-y-12">
            {sections.map((sec, idx) => {
              const key = sec.id ?? sec.heading ?? idx;
              const sectionTitle = sec.title ?? sec.heading;
              const content = sec.content ?? sec.explanation;
              const subHeading = sec.subHeading;
              const secImageUrl = sec.imageUrl;
              const drawioUrl = sec.drawioUrl;

              return (
                <section key={key} id={sec.id || undefined} className="scroll-mt-24">
                  {sectionTitle && (
                    <h2 className="font-display font-bold text-xl sm:text-2xl text-text-hero mb-3">
                      {sectionTitle}
                    </h2>
                  )}
                  {subHeading && (
                    <h3 className="font-display font-semibold text-lg text-text-sub mb-2">{subHeading}</h3>
                  )}
                  {content && (
                    <div
                      className="font-body text-text-body [&_p]:mb-4 [&_strong]:text-cyan [&_strong]:font-semibold"
                      dangerouslySetInnerHTML={{ __html: contentToHtml(content) }}
                    />
                  )}
                  {secImageUrl && (
                    <figure className="my-6 rounded-xl overflow-hidden border bg-forest-card p-4" style={{ borderColor: 'var(--jb-border)' }}>
                      <button type="button" onClick={() => setLightboxSrc(resolveImgUrl(secImageUrl))} className="block w-full text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-forest-bg rounded-lg">
                        <img src={resolveImgUrl(secImageUrl)} alt="" className="w-full h-auto" />
                      </button>
                    </figure>
                  )}
                  {drawioUrl && (
                    <div className="my-6 rounded-xl overflow-hidden border bg-forest-card p-4" style={{ borderColor: 'var(--jb-border)' }}>
                      <button type="button" onClick={() => setLightboxSrc(resolveImgUrl(drawioUrl))} className="block w-full text-left cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-forest-bg rounded-lg">
                        <img src={resolveImgUrl(drawioUrl)} alt="Diagram" className="w-full h-auto" />
                      </button>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {keyTakeaways && keyTakeaways.length > 0 && (
          <section className="mt-12 pt-8 border-t" style={{ borderColor: 'var(--jb-border)' }}>
            <h2 className="font-display font-bold text-xl text-text-hero mb-4">Key takeaways</h2>
            <ul className="font-body text-text-body space-y-2 list-disc list-inside">
              {keyTakeaways.map((takeaway, i) => (
                <li key={i}>{takeaway}</li>
              ))}
            </ul>
          </section>
        )}
      </article>

      {/* Full-size image lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="View full image"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-cyan z-10"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Full size"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
